// «HAY UNA VERSIÓN NUEVA» — la barra que hace que los arreglos LLEGUEN
//
// Ver el porqué entero en `hooks/useVersionNueva`. Resumen: el `index.html` cacheado dejaba a la
// gente clavada en la versión del día que abrió la pestaña, y los arreglos que subíamos no se veían
// salvo en una ventana de incógnito.
//
// POR QUÉ NO RECARGA SOLA AQUÍ: alguien puede estar escribiendo la nota de un paciente. Recargar por
// las buenas le borra lo escrito, y eso es peor que la versión vieja. Quien lo decide es la persona
// — y si la pestaña lleva un cuarto de hora sin tocarse, el hook ya recarga solo, sin barra ni nada.
//
// t315 · EL DISEÑO (20-oct-2026). El dueño: «El anuncio de hay una versión nueva en móvil es súper cutre y no está adaptado,
// debe ser a lo ancho y más bonito». Era una pastilla centrada con una frase y un botón pequeño, igual en el ordenador que
// en el teléfono. Ahora es una tarjeta:
//   · en el ORDENADOR, centrada abajo: el icono, qué pasa y qué hacer antes, «Actualizar» y la X para cerrarla;
//   · en el MÓVIL (el corte de 768 del ANCHO EFECTIVO, el mismo de toda la app: utils/uiScale), A LO ANCHO de la pantalla,
//     abajo y por encima de la barra del sistema (--sa-abajo, como el visor 3D), con «Actualizar» grande para el dedo;
//   · con los colores de la clínica: el botón con su principal (--primary / --sobre-primary) y el icono con su secundario
//     (--secundario / --sobre-secundario), los mismos papeles que tienen en el resto de la app; en oscuro, los tokens ya
//     cambian solos (index.css · html.dark).
// CERRARLA no la apaga para siempre: se aparta media hora (o hasta que salga otra versión más nueva), porque el aviso
// existe para que los arreglos lleguen. La pestaña que se queda quieta un cuarto de hora se actualiza sola igual.
import { useEffect, useState } from 'react'
import { useVersionNueva } from '../hooks/useVersionNueva'
import { Ico } from './ui'
import { t as i18nT } from '../utils/i18n'   // B-2259 · los textos, en el idioma de quien mira (utils/i18n)

// Cuánto se aparta al cerrarla.
export const VOLVER_A_AVISAR_MS = 30 * 60 * 1000

// Los estilos, aquí y no en index.css: son de este aviso y de nadie más. El corte del móvil es la clase que pone
// utils/uiScale en <html> (`a-max-768`, con el ancho dividido por el tamaño del perfil), no un @media: con «Muy grande»
// en un portátil también se pinta a lo ancho. `:where()` deja la especificidad igual que la de la regla de base.
export const ESTILOS_AVISO_VERSION = `
.aviso-version {
  left: 0; right: 0; margin: 0 auto; bottom: calc(16px + var(--sa-abajo));
  width: min(620px, calc((100vw / var(--z, 1)) - 32px)); box-sizing: border-box;
  display: grid; grid-template-columns: auto minmax(0, 1fr) auto auto; grid-template-areas: "icono texto boton cerrar";
  align-items: center; gap: 14px; padding: 12px 10px 12px 14px;
  background: var(--surface); color: var(--text-primary);
  border: 1px solid color-mix(in srgb, var(--primary) 24%, var(--border)); border-radius: 16px;
  box-shadow: 0 18px 44px -14px rgba(15, 23, 42, 0.34), 0 2px 8px rgba(15, 23, 42, 0.08);
  font-family: inherit; animation: avisoVersionEntra 0.32s cubic-bezier(0.16, 1, 0.3, 1) both;
}
html.dark .aviso-version { box-shadow: 0 20px 48px -12px rgba(0, 0, 0, 0.62), 0 2px 8px rgba(0, 0, 0, 0.3); }
.aviso-version__icono {
  grid-area: icono; width: 40px; height: 40px; border-radius: 12px; display: grid; place-items: center;
  background: var(--secundario); color: var(--sobre-secundario);
}
.aviso-version__texto { grid-area: texto; display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.aviso-version__titulo { font-size: 14.5px; font-weight: 800; line-height: 1.3; }
.aviso-version__detalle { font-size: 13px; line-height: 1.45; color: var(--text-secondary); }
.aviso-version__actualizar {
  grid-area: boton; display: inline-flex; align-items: center; justify-content: center; gap: 8px;
  min-height: 42px; padding: 0 18px; border: none; border-radius: 12px; cursor: pointer; white-space: nowrap;
  background: var(--primary); color: var(--sobre-primary, #fff); font: inherit; font-size: 14px; font-weight: 800;
  transition: background-color 0.15s ease, transform 0.12s ease;
}
.aviso-version__actualizar:hover { background: var(--primary-hover); }
.aviso-version__actualizar:active { transform: scale(0.97); }
.aviso-version__actualizar:disabled { opacity: 0.8; cursor: progress; transform: none; }
.aviso-version__cerrar {
  grid-area: cerrar; width: 36px; height: 36px; border: none; border-radius: 10px; cursor: pointer;
  display: grid; place-items: center; background: transparent; color: var(--text-muted);
  transition: background-color 0.15s ease, color 0.15s ease;
}
.aviso-version__cerrar:hover { background: var(--surface-2); color: var(--text-primary); }
.aviso-version__actualizar:focus-visible, .aviso-version__cerrar:focus-visible { outline: 3px solid var(--foco-aro); outline-offset: 2px; }
:where(html.a-max-768) .aviso-version {
  left: calc(10px + var(--sa-izq)); right: calc(10px + var(--sa-der)); width: auto; margin: 0;
  bottom: calc(10px + var(--sa-abajo));
  grid-template-columns: auto minmax(0, 1fr) auto; grid-template-areas: "icono texto cerrar" "boton boton boton";
  align-items: start; gap: 12px; padding: 14px 8px 12px 14px; border-radius: 20px;
}
:where(html.a-max-768) .aviso-version__texto { padding-top: 1px; }
:where(html.a-max-768) .aviso-version__titulo { font-size: 15.5px; }
:where(html.a-max-768) .aviso-version__detalle { font-size: 13.5px; }
:where(html.a-max-768) .aviso-version__actualizar { width: calc(100% - 6px); min-height: 50px; border-radius: 14px; font-size: 16px; }
:where(html.a-max-768) .aviso-version__cerrar { width: 44px; height: 44px; margin: -8px 0 0; border-radius: 12px; }
@keyframes avisoVersionEntra {
  from { opacity: 0; transform: translateY(18px) scale(0.98); }
  to { opacity: 1; transform: none; }
}
@media (prefers-reduced-motion: reduce) { .aviso-version { animation: none; } }
`

export default function AvisoVersionNueva() {
  const nueva = useVersionNueva()
  // La versión para la que se cerró: no vuelve a salir para ESA hasta dentro de media hora; una más nueva, enseguida.
  const [cerrada, setCerrada] = useState(null)
  const [actualizando, setActualizando] = useState(false)
  useEffect(() => {
    if (!cerrada) return undefined
    const t = setTimeout(() => setCerrada(null), VOLVER_A_AVISAR_MS)
    return () => clearTimeout(t)
  }, [cerrada])
  if (!nueva || cerrada === nueva) return null
  const actualizar = () => { setActualizando(true); window.location.reload() }
  return (
    <div role="status" className="aviso-version" data-aviso-version="" style={{ position: 'fixed', zIndex: 99990 }}>
      <style>{ESTILOS_AVISO_VERSION}</style>
      <span className="aviso-version__icono" aria-hidden="true"><Ico n="refresh" s={20} sw={2.2} /></span>
      <span className="aviso-version__texto">
        <strong className="aviso-version__titulo">{i18nT('components.AvisoVersionNueva.hay_una_version_nueva', 'Hay una versión nueva')}</strong>
        <span className="aviso-version__detalle">{i18nT('components.AvisoVersionNueva.trae_los_ultimos_arreglos_de', 'Trae los últimos arreglos de Biango. Si estás escribiendo algo, guárdalo antes de actualizar.')}</span>
      </span>
      <button type="button" className="aviso-version__actualizar" data-actualizar-version="" onClick={actualizar} disabled={actualizando}>
        <Ico n="refresh" s={17} sw={2.4} />{actualizando ? i18nT('components.AvisoVersionNueva.actualizando', 'Actualizando…') : i18nT('components.AvisoVersionNueva.actualizar', 'Actualizar')}
      </button>
      <button type="button" className="aviso-version__cerrar" data-cerrar-version="" onClick={() => setCerrada(nueva)}
        aria-label={i18nT('components.AvisoVersionNueva.cerrar_el_aviso_vuelve_a', 'Cerrar el aviso (vuelve a salir en media hora)')} title={i18nT('components.AvisoVersionNueva.ahora_no', 'Ahora no')}>
        <Ico n="x" s={19} sw={2.2} />
      </button>
    </div>
  )
}
